/**
 * Sự kiện bảng cho anim (BoardView.play), ánh xạ từ sự kiện của resolve/battle thật trong
 * @ml/core (thay resolve tạm `resolveLocal.ts` của s9).
 */
import type { BattleEvent, Coord, FallEvent, ResolveEvent, SpawnEvent } from '@ml/core';

export type BoardEvent =
  | { readonly type: 'pop'; readonly cells: readonly Coord[] }
  | {
      readonly type: 'booster';
      readonly at: Coord;
      readonly kind: string;
      readonly area: readonly Coord[];
      /** Màu chớp (mặc định vàng nhạt). */
      readonly color?: number;
    }
  | { readonly type: 'merge'; readonly at: Coord; readonly charge: number }
  | { readonly type: 'fall'; readonly moves: readonly FallEvent[] }
  | { readonly type: 'spawn'; readonly cells: readonly SpawnEvent[] }
  | { readonly type: 'shuffle'; readonly action: 'shuffled' | 'regenerated' };

const DEBUFF_FLASH: Record<string, number> = {
  lockOrbs: 0xb0b0c0,
  freezeOrbs: 0x9fd8ff,
  deadMark: 0xc04060,
  destroyOrbs: 0xff6040,
};

/** Sự kiện resolve của người chơi → pop / booster / merge (+ fall/spawn/shuffle nếu `settle`). */
function fromResolve(events: readonly ResolveEvent[], settle: boolean, out: BoardEvent[]): void {
  const popped: Coord[] = [];
  let area: Coord[] | null = null;
  for (const e of events) {
    switch (e.type) {
      case 'consume':
        popped.push(...e.cells);
        break;
      case 'activate':
      case 'detonate':
        area = [e.at];
        out.push({
          type: 'booster',
          at: e.at,
          kind: e.type === 'activate' ? e.source : 'essence',
          area,
        });
        break;
      case 'hit':
        if (e.result === 'empty' || e.result === 'shielded') break;
        if (area) area.push(e.at);
        if (e.result === 'destroyed' || e.result === 'defused') popped.push(e.at);
        break;
      case 'merge':
        popped.push(...e.from);
        out.push({ type: 'merge', at: e.at, charge: e.charge });
        break;
      case 'fall':
      case 'spawn':
        if (settle) out.push(e);
        break;
      case 'shuffle':
        out.push({ type: 'shuffle', action: e.action });
        break;
      default:
        break;
    }
  }
  if (popped.length > 0) out.unshift({ type: 'pop', cells: popped });
}

/**
 * Ánh xạ sự kiện một bước battle (P1→P4) sang sự kiện anim bảng. Bảng đích là bảng cuối của
 * bước, nên chỉ lấy fall/spawn của lần settle cuối cùng (P4 nếu có, không thì của resolve).
 */
export function boardEventsOf(events: readonly BattleEvent[]): BoardEvent[] {
  const lateSettle = events.some((e) => e.type === 'fall' || e.type === 'spawn');
  const out: BoardEvent[] = [];
  for (const e of events) {
    if (e.type === 'player') fromResolve(e.events, !lateSettle, out);
    else if (e.type === 'boardDebuff') {
      out.push({
        type: 'booster',
        at: e.coords[0] ?? { x: 0, y: 0 },
        kind: e.effect,
        area: e.coords,
        color: DEBUFF_FLASH[e.effect],
      });
      if (e.effect === 'destroyOrbs') out.push({ type: 'pop', cells: e.coords });
    } else if (e.type === 'deadMarkExplode') {
      out.push({ type: 'booster', at: e.at, kind: 'deadMark', area: [e.at], color: 0xc04060 });
    } else if (e.type === 'fall' || e.type === 'spawn') out.push(e);
    else if (e.type === 'shuffle') out.push({ type: 'shuffle', action: e.action });
  }
  return out;
}
